import { ProfileCard } from '@/components/common/ProfileCard';
import { ProfileFact, ProfileFacts } from '@/components/common/ProfileFacts';
import { ProfileChipList } from '@/components/common/ProfileChip';
import { Button } from '@/components/ui/Button';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import { useCountries } from '@/hooks/useCountries';
import { useStates } from '@/hooks/useStates';
import { contactTitleLabel } from '@/lib/constants/contact-titles';
import { displayUrl, joinTruthy } from '@/lib/utils/format.utils';
import { formatPhoneDisplay } from '@/lib/utils/phone.utils';
import { INVESTOR_CARD_STRINGS as C } from './investor-form.constants';
import { investorTypeLabel } from './investor-options';
import type { InvestorCardProps } from './InvestorPreferenceCards';

const LINK_CLASS = 'text-dkgreen hover:text-dkgreen-hover';

function externalLink(href?: string | null, label?: string) {
  if (!href) return undefined;
  return (
    <a href={href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
      {label ?? displayUrl(href)}
    </a>
  );
}

/**
 * About (full width, facts four to a row): who to contact at the fund. The
 * bio is the header's description, so it is not repeated here.
 */
export function InvestorAboutCard({ data, action }: InvestorCardProps) {
  return (
    <ProfileCard title={C.ABOUT} action={action}>
      <ProfileFacts columns={4}>
        <ProfileFact label={C.CONTACT} value={data.contactName} />
        <ProfileFact label={C.TITLE} value={contactTitleLabel(data.contactTitle)} />
        <ProfileFact label={C.INVESTOR_TYPE} value={investorTypeLabel(data.investorType)} />
        <ProfileFact label={C.PHONE} value={formatPhoneDisplay(data.phone)} />
        <ProfileFact label={C.EMAIL} value={data.email} truncate />
        <ProfileFact label={C.WEBSITE} truncate>
          {externalLink(data.websiteUrl)}
        </ProfileFact>
        <ProfileFact label={C.LINKEDIN}>{externalLink(data.linkedinUrl, C.LINKEDIN)}</ProfileFact>
      </ProfileFacts>
    </ProfileCard>
  );
}

/** Location (mockup: 7 of 12 columns, facts three to a row, the address across two). */
export function InvestorLocationCard({ data, action }: InvestorCardProps) {
  return (
    <ProfileCard title={C.LOCATION} action={action}>
      <ProfileFacts columns={3}>
        <ProfileFact
          label={C.ADDRESS}
          value={joinTruthy([data.addressStreet, data.addressStreet2])}
          span={2}
        />
        <ProfileFact label={C.POSTAL_CODE} value={data.addressPostalCode} />
        <ProfileFact label={C.CITY} value={data.addressCity} />
        <ProfileFact label={C.STATE} value={data.addressState} />
        <ProfileFact label={C.COUNTRY} value={data.addressCountry} />
      </ProfileFacts>
    </ProfileCard>
  );
}

function useGeographyLabels(data: InvestorCardProps['data']): string[] {
  const { data: countries = [] } = useCountries();
  const { data: allStates = [] } = useStates(undefined);
  return (data.geographies ?? [])
    .map((geography) => {
      const country = countries.find((row) => row.id === geography.countryId)?.name;
      const state = allStates.find((row) => row.id === geography.stateId)?.name;
      return joinTruthy([geography.worldRegion, joinTruthy([state, country])], ' · ');
    })
    .filter(Boolean);
}

/**
 * Preferred geographies (mockup: 5 of 12 columns), always in the mockup's
 * dashed amber note style (client feedback 2026-10-15). Empty for an editor:
 * a prompt with an Add action (FR-175).
 */
export function InvestorGeographiesCard({ data, action, onAdd }: InvestorCardProps) {
  const labels = useGeographyLabels(data);
  if (labels.length === 0 && onAdd) {
    return (
      <ProfileCard title={C.GEOGRAPHIES} tone="attention">
        <p className="text-on-yellow-muted mb-4 text-sm leading-[1.3125rem]">
          {C.GEOGRAPHIES_EMPTY}
        </p>
        <Button variant="outline-sm-accent" className="w-fit" onClick={onAdd}>
          {C.ADD_GEOGRAPHY}
        </Button>
      </ProfileCard>
    );
  }
  return (
    <ProfileCard title={C.GEOGRAPHIES} action={action} tone="attention">
      {labels.length > 0 ? (
        <ProfileChipList items={labels} />
      ) : (
        <p className="text-input text-subtext font-medium">{L.NOT_PROVIDED}</p>
      )}
    </ProfileCard>
  );
}
